import styled from "styled-components";
import { useState } from "react";

import { SectionHeading } from "@/components/Layout/Parts";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
} from "@/components/Table";

import {
  SortState,
  TestingCompetitionParticipantsResource,
} from "@/types/resources";
import { SortDirection } from "@/utils/enums";
import ParticipantsPerformanceTableRow from "./ParticipantsPerformanceTableRow";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/api";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "totalWins",
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    width: 19%;
    font-weight: 700;
    &:nth-child(1) {
      width: 24%;
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const ParticipantsPerformanceTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const { data, isLoading, isError } = useQuery<{
    participants: TestingCompetitionParticipantsResource[];
    winnersByWins: number[];
    winnersByCtr: number[];
  }>(
    ["participants", { sort }],
    () =>
      api.get<
        never,
        {
          participants: TestingCompetitionParticipantsResource[];
          winnersByWins: number[];
          winnersByCtr: number[];
        }
      >("/v1/gamification/thumbnails/participants", {
        params: {
          ...(sort.direction !== "DEFAULT" && {
            sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
          }),
        },
      }),
    {
      refetchOnMount: false,
      staleTime: 1000 * 120,
    }
  );

  return (
    <>
      <SectionHeading>All participants performance</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                property="name"
                title="Participant Name"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="currentCompetitions"
                title="Current Competitions"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="totalVariations"
                title="Total Variations"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="maxCtr"
                title="Highest CTR %"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="totalWins"
                title="Total Wins"
                setSort={setSort}
                sort={sort}
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={5}
              isError={isError}
              hasData={!!data?.participants?.length}
            >
              {data?.participants?.map((participant, index) => (
                <ParticipantsPerformanceTableRow
                  key={index}
                  participant={participant}
                  winnersByWins={data?.winnersByWins}
                  winnersByCtr={data?.winnersByCtr}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
    </>
  );
};

export default ParticipantsPerformanceTable;
